<template>
  <section class="section">
    <div class="report-header">
      <div>
        <h1>Payroll Reports</h1>
        <p class="muted">Display and export approved clock-in/out payroll records.</p>
      </div>

      <button class="export-button" :disabled="!employees.length" @click="exportCsv">
        Export CSV
      </button>
    </div>

    <div class="filters">
      <label>
        Start date
        <input v-model="filters.start" type="date" :max="filters.end" />
      </label>

      <label>
        End date
        <input v-model="filters.end" type="date" :min="filters.start" />
      </label>

      <label>
        Currency
        <select v-model="selectedCurrency">
          <option v-for="currency in currencies" :key="currency" :value="currency">
            {{ currency }}
          </option>
        </select>
      </label>

      <button class="button" :disabled="loading" @click="loadReport">
        {{ loading ? 'Loading...' : 'Generate report' }}
      </button>
    </div>

    <div v-if="errorMessage" class="alert error">
      {{ errorMessage }}
    </div>

    <div v-if="summary" class="summary-grid">
      <div class="summary-card">
        <span>Total employees</span>
        <strong>{{ summary.employeeCount }}</strong>
      </div>

      <div class="summary-card">
        <span>Total hours</span>
        <strong>{{ summary.totalHours }}</strong>
      </div>

      <div class="summary-card">
        <span>Total payroll</span>
        <strong>{{ formatCurrency(summary.totalPay) }}</strong>
      </div>
    </div>

    <div v-if="employees.length" class="report-table-wrap">
      <table class="report-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>Email</th>
            <th>Hours</th>
            <th>Days</th>
            <th>Rate/hour</th>
            <th>Rate/day</th>
            <th>Hourly pay</th>
            <th>Daily pay</th>
            <th>Total pay</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="employee in employees" :key="employee.userId">
            <td>{{ employee.name }}</td>
            <td>{{ employee.email }}</td>
            <td>{{ employee.totalHours }}</td>
            <td>{{ employee.daysWorked }}</td>
            <td>{{ formatCurrency(employee.rateperhour) }}</td>
            <td>{{ formatCurrency(employee.rateperday) }}</td>
            <td>{{ formatCurrency(employee.hourlyPay) }}</td>
            <td>{{ formatCurrency(employee.dailyPay) }}</td>
            <td><strong>{{ formatCurrency(employee.totalPay) }}</strong></td>
          </tr>
        </tbody>
      </table>
    </div>

    <p v-else-if="searched && !loading" class="muted">
      No approved clock-in/out records found for this period.
    </p>
  </section>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import { GET_PAYROLL_CLOCKINOUTS, UPDATE_USER } from '@/hooks/queries'

const currencies = Intl.supportedValuesOf('currency')

const user = JSON.parse(localStorage.getItem('user') || '{}')

const selectedCurrency = ref(user.currency || 'USD')
const currencyLoaded = ref(false)

const { mutate: updateUser } = useMutation(UPDATE_USER)

function getSemiMonthlyPeriod() {
  const today = new Date()
  const year = today.getFullYear()
  const month = today.getMonth()
  const currentDay = today.getDate()

  let startDate
  let endDate

  if (currentDay <= 15) {
    startDate = new Date(year, month, 1)
    endDate = new Date(year, month, 15)
  } else {
    startDate = new Date(year, month, 16)
    endDate = new Date(year, month + 1, 0)
  }

  return {
    start: formatDateForInput(startDate),
    end: formatDateForInput(endDate),
  }
}

function formatDateForInput(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const defaultPeriod = getSemiMonthlyPeriod()

const filters = reactive({
  start: defaultPeriod.start,
  end: defaultPeriod.end,
})

const employees = ref([])
const searched = ref(false)
const errorMessage = ref('')

const queryVariables = computed(() => ({
  admin: String(user.id),
  start: filters.start ? `${filters.start}T00:00:00.000Z` : null,
  end: filters.end ? `${filters.end}T23:59:59.999Z` : null,
}))

const {
  result,
  loading,
  error,
  refetch,
} = useQuery(GET_PAYROLL_CLOCKINOUTS, queryVariables)

const summary = computed(() => {
  if (!employees.value.length) return null

  const totalHours = employees.value.reduce(
    (sum, employee) => sum + employee.totalHours,
    0
  )

  const totalPay = employees.value.reduce(
    (sum, employee) => sum + employee.totalPay,
    0
  )

  return {
    employeeCount: employees.value.length,
    totalHours: Number(totalHours.toFixed(2)),
    totalPay: Number(totalPay.toFixed(2)),
  }
})

watch(result, (value) => {
  const records = value?.clockinouts || []
  employees.value = buildPayrollReport(records)
})

watch(error, (value) => {
  if (value) {
    errorMessage.value = value.message
  }
})

watch(selectedCurrency, async (currency) => {
  if (!currencyLoaded.value) return
  if (!user?.documentId) return

  try {
    await updateUser({
      documentId: user.documentId,
      data: {
        currency,
      },
    })

    const updatedUser = {
      ...user,
      currency,
    }

    localStorage.setItem('user', JSON.stringify(updatedUser))
  } catch (err) {
    errorMessage.value = err.message || 'Unable to update currency.'
  }
})

async function loadReport() {
  errorMessage.value = ''

  if (filters.start > filters.end) {
    errorMessage.value = 'Start date cannot be after end date.'
    return
  }

  if (!filters.start || !filters.end) {
    errorMessage.value = 'Please select start and end dates.'
    return
  }

  if (!user?.id) {
    errorMessage.value = 'Unable to identify logged-in admin.'
    return
  }

  searched.value = true

  try {
    await refetch(queryVariables.value)
  } catch (err) {
    errorMessage.value = err.message || 'Unable to generate payroll report.'
  }
}

function buildPayrollReport(records) {
  const grouped = {}

  records.forEach((record) => {
    if (!record.in || !record.out || !record.users_permissions_user) return

    const employeeUser = record.users_permissions_user
    const userId = employeeUser.documentId

    const clockIn = new Date(record.in)
    const clockOut = new Date(record.out)
    const milliseconds = clockOut.getTime() - clockIn.getTime()

    if (milliseconds <= 0) return

    const hours = milliseconds / (1000 * 60 * 60)
    const workDate = clockIn.toISOString().slice(0, 10)

    const ratePerHour = Number(employeeUser.rateperhour || 0)
    const ratePerDay = Number(employeeUser.rateperday || 0)

    if (!grouped[userId]) {
      grouped[userId] = {
        userId,
        name:
          employeeUser.fullname ||
          employeeUser.username ||
          employeeUser.email ||
          'Unknown employee',
        email: employeeUser.email || '',
        rateperhour: ratePerHour,
        rateperday: ratePerDay,
        totalHours: 0,
        days: new Set(),
        hourlyPay: 0,
        dailyPay: 0,
        totalPay: 0,
        shifts: [],
      }
    }

    grouped[userId].totalHours += hours
    grouped[userId].days.add(workDate)
    grouped[userId].hourlyPay += hours * ratePerHour

    const shiftPay = ratePerHour > 0 ? hours * ratePerHour : 0

    grouped[userId].shifts.push({
      id: record.documentId,
      date: workDate,
      in: record.in,
      out: record.out,
      hours: Number(hours.toFixed(2)),
      decision: record.decision || '',
      carehome: formatCarehome(record.carehome),
      note: record.note || '',
      pay: Number(shiftPay.toFixed(2)),
    })
  })

  return Object.values(grouped).map((employee) => {
    const daysWorked = employee.days.size

    employee.totalHours = Number(employee.totalHours.toFixed(2))
    employee.hourlyPay = Number(employee.hourlyPay.toFixed(2))
    employee.dailyPay = Number((daysWorked * employee.rateperday).toFixed(2))
    employee.totalPay =
      employee.rateperhour > 0 ? employee.hourlyPay : employee.dailyPay
    employee.daysWorked = daysWorked

    delete employee.days

    return employee
  })
}

function formatCarehome(carehome) {
  if (!carehome) return ''

  return [carehome.housenumber, carehome.streetname]
    .filter(Boolean)
    .join(' ')
}

function exportCsv() {
  const rows = [
    [
      'Employee',
      'Email',
      'Total Hours',
      'Days Worked',
      `Rate Per Hour (${selectedCurrency.value})`,
      `Rate Per Day (${selectedCurrency.value})`,
      `Hourly Pay (${selectedCurrency.value})`,
      `Daily Pay (${selectedCurrency.value})`,
      `Total Pay (${selectedCurrency.value})`,
    ],
  ]

  employees.value.forEach((employee) => {
    rows.push([
      employee.name,
      employee.email,
      employee.totalHours,
      employee.daysWorked,
      employee.rateperhour,
      employee.rateperday,
      employee.hourlyPay,
      employee.dailyPay,
      employee.totalPay,
    ])
  })

  const csv = rows
    .map((row) =>
      row
        .map((value) => `"${String(value ?? '').replaceAll('"', '""')}"`)
        .join(',')
    )
    .join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = `payroll-report-${filters.start}-to-${filters.end}.csv`
  link.click()

  URL.revokeObjectURL(url)
}

function formatCurrency(value) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: selectedCurrency.value || 'USD',
  }).format(Number(value || 0))
}

onMounted(() => {
  selectedCurrency.value = user.currency || 'USD'
  currencyLoaded.value = true
  loadReport()
})

watch(
  () => [filters.start, filters.end],
  () => {
    if (filters.start && filters.end) {
      loadReport()
    }
  }
)
</script>

<style scoped>
.section {
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.report-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;
}

.report-header h1 {
  margin-bottom: 0.4rem;
  font-size: 2.2rem;
  font-weight: 800;
}

.muted {
  color: #6b7280;
}

.filters {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
}

.filters .button {
  height: 48px;
  margin-bottom: 1px;
}

.filters label {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-weight: 600;
  color: #374151;
}

.filters input {
  min-width: 230px;
  height: 48px;
  padding: 0 0.9rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.8rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}

.summary-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 1.2rem;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.04);
}

.summary-card span {
  display: block;
  color: #6b7280;
  font-size: 0.9rem;
  margin-bottom: 0.4rem;
}

.summary-card strong {
  display: block;
  font-size: 1.6rem;
  color: #111827;
}

.report-table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  background: white;
}

.report-table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
}

.report-table th {
  background: #f9fafb;
  color: #374151;
  font-size: 0.85rem;
  font-weight: 700;
  text-align: left;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.report-table td {
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #111827;
  white-space: nowrap;
}

.report-table tr:hover td {
  background: #f9fafb;
}

.button {
  border: none;
  border-radius: 0.8rem;
  background: #2563eb;
  color: white;
  padding: 0.8rem 1.2rem;
  font-weight: 700;
  cursor: pointer;
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.export-button {
  border-radius: 0.8rem;
  background: white;
  color: #2563eb;
  border: 1px solid #2563eb;
}

.export-button:hover {
  background: #eff6ff;
}

.alert.error {
  background: #fee2e2;
  color: #991b1b;
  border-radius: 0.8rem;
  padding: 1rem;
  margin-bottom: 1rem;
}

@media (max-width: 800px) {
  .report-header {
    flex-direction: column;
  }

  .summary-grid {
    grid-template-columns: 1fr;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .filters input,
  .filters button {
    width: 100%;
  }
}
</style>
